<template>
  <q-page padding>
    <AuthenticateLayout withHeader >
      <template v-slot:title>{{ t('activate_account') }}</template>
      <template v-slot:heading>
        <div class="row no-wrap items-center">
          <div class="col">
            {{ heading }}
          </div>
          <div class="col-auto">
            <zbm-btn-icon @click="showCreatePasswordInfoDialog = true"
                          icon="fa-light fa-circle-question"
                          size="30px"
                          data-testid="help" />
          </div>
        </div>
      </template>

      <template v-slot:default>
        <zbm-dialog v-model="showCreatePasswordInfoDialog">
          <div class="row q-dialog__header">
            <div class="col-auto">
              <q-icon color="dark-teal" name="fa-solid fa-circle-question" size="42px" />
            </div>
          </div>

          <div class="row q-dialog__body">
            <div class="col">
              <h3 class="text-weight-semibold q-mb-lg">{{ t('sign_up_help') }}</h3>
              <div class="support-info-text" v-html="t('activate_auth_help_mymobility_phone_email_pwe', [
                  sessionStore.supportInfo?.countrySupportPhone,
                  sessionStore.supportInfo?.countrySupportEmail,
                  sessionStore.supportInfo?.patientSupportUrl,
                  supportUrlOrProfileInfo])"
              ></div>
            </div>
          </div>

          <div class="row q-dialog__footer">
            <div class="col-auto">
              <zbm-btn
                v-close-popup
                :color="$zb.enums.ButtonStdColorEnum.accentText"
                :height="$zb.enums.ButtonHeightEnum.lg"
                :label="t('gen_okay')"
                data-testid="okay"
              />
            </div>
          </div>
  </zbm-dialog>
        <PasswordConfirmWithValidation auto-focus ref="passwordComponent" v-model="passwordResult"
          @validate="validateResult"
          :initialPlaceholder="hasSimplePatientPasscode ? 'activate_enter_passcode' : 'password_create'"
          :confirmationPlaceholder="hasSimplePatientPasscode ? 'activate_confirm_passcode' : 'password_confirm'"
          :hasPin="hasSimplePatientPasscode" />
      </template>

      <template v-slot:actions>
        <ProgressBar :backEnabled="false" :progress="91" :nextEnabled="passwordResult.isValid"
          @on-next-click="onNextClick" />
      </template>
    </AuthenticateLayout>
  </q-page>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { makeEmptyPasswordConfirmWithValidationResult, type PasswordConfirmWithValidationResult } from 'src/utils/componentFactories';
import { useActivationStore } from 'src/store';
import { useSessionStore } from '@/store/sessionStore';
import { ClientFeatureType } from 'src/constants/enums';
import { handleApiRequest } from 'src/api/handleApiRequest';
import { createPassword, getRegistrationNextStepSkipLoginWithExting } from 'src/api/endpoints/registrationWeb';
import { translateStepToRoute } from 'src/utils/interfaceUtils';
import AuthenticateLayout from 'src/components/layouts/AuthenticateLayout.vue';
import ProgressBar from 'src/components/ProgressBar.vue';
import PasswordConfirmWithValidation from 'src/components/form/PasswordConfirmWithValidation.vue';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';

const showCreatePasswordInfoDialog = ref(false);
const router = useRouter();
const previousRoute = ref('');
const sessionStore = useSessionStore();
const activationStore = useActivationStore()
const $q = useQuasar(),
  {t} = useI18n();

const passwordComponent = ref(null);
const passwordResult = ref(makeEmptyPasswordConfirmWithValidationResult());

const hasSimplePatientPasscode = computed(() => sessionStore.hasPasscode);
const supportUrlOrProfileInfo = computed(() =>  t('activate_auth_help_click_here_pwe'));

const heading = computed(() => {
  if (hasSimplePatientPasscode.value) {
    return t('activate_create_passcode').toString();
  } else {
    return t('password_new_create').toString();
  }
});

onMounted(() => {
  sessionStore.setIsHeaderVisible(true);
});

async function onNextClick() {
  if (passwordResult.value.isValid) {
    $q.loading.show()
    const password = passwordResult.value.password;
    const response = await handleApiRequest(() => createPassword(password), null, $q);
    if (response?.result) {
      activationStore.setRegistrationPassword(passwordResult.value.password);

      const step = await handleApiRequest(() => getRegistrationNextStepSkipLoginWithExting(), null, $q);
      const nextStep = translateStepToRoute(step?.result as string);
      $q.loading.hide()
      router.push({ name: nextStep });
    } else {
      $q.loading.hide()
    }
  }
}

async function validateResult(result: PasswordConfirmWithValidationResult) {
  passwordResult.value.password = result.password;
  passwordResult.value.confirmPassword = result.confirmPassword;
  passwordResult.value.isValid = result.isValid;
}

</script>

<style lang="scss" scoped>
    .support-info-text {
        word-wrap: break-word;
    }
</style>
